/**
 * What a figure opens to, and whether the rows under it add up (REQ-021 AC-010,
 * AC-009).
 *
 * `backend/src/services/dashboard/trace.ts` answers in one of six shapes, and
 * the shapes are the design: a refusal, a withholding and an outage are three
 * different facts about why there is no table, and a screen that drew any of
 * them as an empty list would say "composed of nothing" about a figure nobody
 * was allowed to look at. The types below mirror that module exactly and add
 * nothing — no reason text is written on this side, because a paraphrase of a
 * policy goes on reading convincingly after the policy has changed.
 *
 * The one judgement this file makes is the reconciliation. The backend
 * guarantees that the rows it serves sum to the figure they explain, and a
 * reader opens a trace precisely to check that claim. Checking it is therefore
 * this screen's job rather than the reader's: a table that is quietly one row
 * short is the failure a trace exists to catch, and it is invisible to anybody
 * who is not adding up fifty numbers by hand.
 */
import { HttpError } from "@/lib/http";
import type { MetricDescription, MetricUnit, MetricValue } from "./metrics";

export type TraceEntryKind = "subscription" | "account" | "agent";

export interface TraceEntry {
  kind: TraceEntryKind;
  /** The handle the record is looked up by in the store that owns it. */
  ref: string;
  /** What the record contributes. Never a name, an email or an address. */
  fields: Record<string, string | number | boolean | null>;
}

export interface TracePart {
  label: string;
  /** Null where this component could not be counted at all — `reason` says so. */
  value: number | null;
  source: string;
  reason?: string;
}

/** What one record contributes to the figure, as the backend states it. */
export type TraceContribution =
  | { kind: "sum"; field: string }
  | { kind: "count" };

export type TraceDisclosure =
  | {
      status: "records";
      entries: TraceEntry[];
      total: number;
      truncated: boolean;
      contribution: TraceContribution;
      reconciliation: string;
    }
  | { status: "aggregate"; parts: TracePart[]; reason: string }
  | { status: "derived"; from: string[]; reason: string }
  | { status: "refused"; reason: string; policy: string }
  | { status: "withheld"; reason: string; requiredRoles: string[] }
  | { status: "unavailable"; reason: string };

export interface TracePayload {
  metric: MetricDescription;
  value: MetricValue;
  sampleSize: number;
  disclosure: TraceDisclosure;
}

export interface TraceMeta {
  computedAt: string;
  disclosure: TraceDisclosure["status"];
  owner: string;
}

/**
 * The fields a record table knows how to draw.
 *
 * An allow-list rather than whatever keys the rows arrive with. The backend
 * sends commercial fields only and is at pains to keep it that way, but a
 * screen that renders every key it is handed would display the first field
 * somebody adds to a projection without anybody deciding it belonged on a
 * dashboard — and this screen's whole premise is that health-adjacent detail
 * never reaches it. An unrecognised field is therefore named and not drawn.
 */
const DISPLAYED_FIELDS: readonly string[] = [
  "tier",
  "status",
  "stripeCustomerId",
  "name",
  "monthlyEur",
  "spentThisMonthUsd",
  "monthlyBudgetUsd",
];

/**
 * Money is stored to the cent, so a sum of fifty two-decimal numbers may miss
 * the figure by a float's width. Half a cent is below anything either side
 * keeps and far below anything worth telling a reader about.
 */
const TOLERANCE = 0.005;

export type Reconciliation =
  | {
      status: "reconciles" | "disagrees";
      /** Summed contributions, or one per record where there is nothing to sum. */
      kind: "sum" | "count";
      /** What the rows come to. */
      rows: number;
      /** The figure they are supposed to come to. */
      figure: number;
      field?: string;
    }
  | { status: "uncheckable"; reason: string };

/**
 * Whether the rows add up to the figure above them.
 *
 * Three answers, and the third is not a polite version of the first. A sum over
 * a truncated table is a sum of the rows that were served, and reporting it as
 * agreeing with the figure would turn "we showed you fifty of four hundred"
 * into "we checked four hundred". Likewise a figure that was never measured has
 * nothing for the rows to disagree with — the records may be perfectly real,
 * and only the arithmetic is missing.
 *
 * What is summed is the backend's stated `contribution` and never a guess from
 * the field names: every subscription row carries `monthlyEur`, including the
 * rows served for the customer *counts*, so a screen that looked for a money
 * field would add three customers up to €29.97 and report that a figure of 3
 * did not reconcile. A count is checked against `total`, which is the whole
 * composition rather than the rows served, so truncation does not stop it being
 * checkable — a wrong count above fifty customers is exactly the case a trace
 * exists to catch.
 */
export function reconcile(
  disclosure: Extract<TraceDisclosure, { status: "records" }>,
  value: MetricValue,
): Reconciliation {
  if (value.status !== "ok") {
    return {
      status: "uncheckable",
      reason:
        "The figure itself was not measured on this read, so there is nothing " +
        "for these records to add up to. They are what the store held; that " +
        "they are the records behind a number is not something this screen can " +
        "show, because there is no number.",
    };
  }

  // Typed as present and read as if it might not be: the payload is a network
  // response, and a body missing the one field the check depends on is a thing
  // to say out loud rather than to fall through into an inference.
  const contribution: TraceContribution | undefined = disclosure.contribution;

  if (contribution?.kind === "count") {
    // One record, one unit of the figure. `total` is the full count and not the
    // served one, so truncation does not stop this being checkable.
    return {
      status: disclosure.total === value.value ? "reconciles" : "disagrees",
      kind: "count",
      rows: disclosure.total,
      figure: value.value,
    };
  }

  if (contribution?.kind !== "sum") {
    // Not "the rows are wrong": the response did not say what they contribute,
    // so there is no arithmetic to do. Guessing one from the fields present is
    // the defect this branch replaced.
    return {
      status: "uncheckable",
      reason:
        "The composition did not say what each record contributes, so the rows " +
        "below cannot be added up against the figure. That is a defect in the " +
        "response rather than a disagreement between the records and the number.",
    };
  }

  const field = contribution.field;

  if (
    !disclosure.entries.every(
      (entry) => typeof entry.fields[field] === "number",
    )
  ) {
    return {
      status: "uncheckable",
      reason:
        `The records are said to contribute \`${field}\`, and not every row ` +
        "carries it as a number. Summing the ones that do would check the " +
        "figure against part of its composition and call it checked.",
    };
  }

  if (disclosure.truncated) {
    return {
      status: "uncheckable",
      reason:
        `Only ${disclosure.entries.length} of ${disclosure.total} records were ` +
        "served — a trace is an explanation and not an export — so what the " +
        "rows below come to is a sum of part of the composition and cannot be " +
        "checked against the figure.",
    };
  }

  const rows = disclosure.entries.reduce(
    (sum, entry) => sum + Number(entry.fields[field] ?? 0),
    0,
  );

  return {
    status:
      Math.abs(rows - value.value) < TOLERANCE ? "reconciles" : "disagrees",
    kind: "sum",
    rows,
    figure: value.value,
    field,
  };
}

/** The unit a reconciliation is written in: a count of records is a count. */
export const reconciliationUnit = (
  reconciliation: Reconciliation,
  unit: MetricUnit,
): MetricUnit =>
  reconciliation.status !== "uncheckable" && reconciliation.kind === "count"
    ? "count"
    : unit;

/**
 * The columns a record table draws, and the fields it will not draw.
 *
 * Both halves are returned, because the third option — dropping an unknown
 * field silently — is the one that must not exist. A row carrying something
 * this screen does not recognise is either a backend change nobody told the
 * dashboard about or a projection that has started returning more than it
 * should, and neither is discovered by a table that quietly leaves it out.
 */
export function entryColumns(entries: TraceEntry[]): {
  columns: string[];
  unknown: string[];
} {
  const columns: string[] = [];
  const unknown: string[] = [];
  for (const entry of entries) {
    for (const key of Object.keys(entry.fields)) {
      const seen = DISPLAYED_FIELDS.includes(key) ? columns : unknown;
      if (!seen.includes(key)) seen.push(key);
    }
  }
  return { columns, unknown };
}

/** `monthlyEur` as a column heading. The backend's own names, spaced out. */
export const fieldLabel = (field: string): string =>
  field
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (first) => first.toUpperCase());

/**
 * A 200 whose body carries no disclosure. Named for the same reason the
 * failures below are: a response this screen could not read is a defect with an
 * owner, and rendering it as a figure with nothing behind it files it where
 * nobody looks.
 */
export const MALFORMED_TRACE =
  "The server answered without a composition in it. Nothing could be read, and this is not the same as a figure with nothing behind it.";

/** The six shapes this screen draws. Anything else is named, not ignored. */
const DISCLOSURE_STATUSES: readonly string[] = [
  "records",
  "aggregate",
  "derived",
  "refused",
  "withheld",
  "unavailable",
];

/**
 * A disclosure whose shape this screen does not know.
 *
 * The same failure as a missing composition and a step further from being
 * noticed: every branch below simply fails to match, so a header saying what
 * composes €4,400.00/mo stands over an empty panel — a figure reading as
 * composed of nothing, which is the one answer this screen must never give by
 * accident.
 */
export const isKnownDisclosure = (disclosure: TraceDisclosure): boolean =>
  DISCLOSURE_STATUSES.includes(disclosure.status);

export const unrecognisedTrace = (status: string): string =>
  `The server described this composition as "${status}", which this screen has ` +
  "no way to draw. Nothing below is missing because the figure is composed of " +
  "nothing — this screen and the server disagree about the answer's shape, and " +
  "that is a defect with an owner.";

/**
 * Why the composition could not be read — never "nothing composes this figure".
 *
 * A 404 says the registry has no such metric and a 400 says this screen asked
 * wrongly. Both are defects with owners, and both are separate from every one
 * of the six answers the endpoint gives when it did understand the question.
 */
export function traceFailure(error: unknown): string {
  const status =
    error instanceof HttpError ? error.response?.status : undefined;

  if (status === 404) {
    return "The server has no metric defined under this id, so it could not say what composes it. That is a gap in the metric registry, not a figure composed of nothing.";
  }
  if (status === 400) {
    return "The composition was requested without naming a metric. Nothing was read, and the fault is on this screen rather than in the data.";
  }
  return "The composition of this figure could not be read. Nothing was read at all — which is not a figure with nothing behind it, and not a refusal to show it.";
}
